import type { ImageBorder } from '../domain/types';

export interface ImageBorderRasterLayout {
  outerWidth: number;
  outerHeight: number;
  contentWidth: number;
  contentHeight: number;
  paddingPx: number;
  layers: Array<ImageBorder & { widthPx: number }>;
}

export interface ImagePixelBuffer {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

const MAX_IMAGE_BORDERS = 3;
const OPAQUE_ALPHA_THRESHOLD = 1;

/** Returns the visible layers in persisted order: artwork-adjacent to outermost. */
export function activeImageBorders(borders: readonly ImageBorder[] | undefined): ImageBorder[] {
  return (borders ?? [])
    .slice(0, MAX_IMAGE_BORDERS)
    .filter(border => Number.isFinite(border.widthMm) && border.widthMm > 0)
    .map(border => ({ colour: border.colour, widthMm: border.widthMm }));
}

/**
 * Keeps an image object's physical bounding box stable when borders are added.
 * Border layers consume an inset inside that box and the artwork is fitted into
 * the remaining content rectangle. Excessive widths are proportionally clamped.
 */
export function createImageBorderRasterLayout(
  outerWidth: number,
  outerHeight: number,
  borders: readonly ImageBorder[] | undefined,
  pixelsPerMm: number,
): ImageBorderRasterLayout {
  const width = Math.max(1, Math.round(outerWidth));
  const height = Math.max(1, Math.round(outerHeight));
  const active = activeImageBorders(borders);
  const totalMm = active.reduce((sum, border) => sum + border.widthMm, 0);
  const maximumPadding = Math.max(0, Math.floor((Math.min(width, height) - 1) / 2));
  const requestedPadding =
    totalMm > 0 && pixelsPerMm > 0 ? Math.max(1, Math.round(totalMm * pixelsPerMm)) : 0;
  const paddingPx = Math.min(requestedPadding, maximumPadding);
  const effectivePixelsPerMm = totalMm > 0 ? paddingPx / totalMm : 0;

  return {
    outerWidth: width,
    outerHeight: height,
    contentWidth: Math.max(1, width - paddingPx * 2),
    contentHeight: Math.max(1, height - paddingPx * 2),
    paddingPx,
    layers: active.map(border => ({
      ...border,
      widthPx: border.widthMm * effectivePixelsPerMm,
    })),
  };
}

/**
 * Builds alpha-aware, stacked outline rings around a cropped image raster.
 * The source must have the layout's content dimensions. The returned buffer
 * always has the exact outer dimensions, which makes editor and print bounds
 * deterministic and prevents borders from being clipped during PDF cropping.
 */
export function composeImageWithStackedBorders(
  source: ImagePixelBuffer,
  layout: ImageBorderRasterLayout,
): ImagePixelBuffer {
  if (source.width !== layout.contentWidth || source.height !== layout.contentHeight) {
    throw new RangeError('Image border source dimensions do not match the raster layout');
  }
  if (source.data.length !== source.width * source.height * 4) {
    throw new RangeError('Image border source pixel buffer has an invalid length');
  }

  const output = new Uint8ClampedArray(layout.outerWidth * layout.outerHeight * 4);
  const sourceOffsetX = layout.paddingPx;
  const sourceOffsetY = layout.paddingPx;
  const layers = layout.layers.filter(layer => layer.widthPx > 0);

  if (layers.length > 0 && containsVisiblePixels(source.data)) {
    const distance = alphaDistanceMap(
      source,
      layout.outerWidth,
      layout.outerHeight,
      sourceOffsetX,
      sourceOffsetY,
    );
    const colours = layers.map(layer => parseColour(layer.colour));
    const thresholds: number[] = [];
    layers.reduce((sum, layer) => {
      const cumulative = sum + layer.widthPx;
      thresholds.push(cumulative);
      return cumulative;
    }, 0);

    for (let pixel = 0; pixel < distance.length; pixel += 1) {
      const measured = distance[pixel]!;
      const layerIndex = thresholds.findIndex(threshold => measured <= threshold + 0.5);
      if (layerIndex < 0) continue;
      const colour = colours[layerIndex]!;
      const target = pixel * 4;
      output[target] = colour[0];
      output[target + 1] = colour[1];
      output[target + 2] = colour[2];
      output[target + 3] = colour[3];
    }
  }

  compositeSourceOver(source, output, layout.outerWidth, sourceOffsetX, sourceOffsetY);
  return { width: layout.outerWidth, height: layout.outerHeight, data: output };
}

function containsVisiblePixels(data: Uint8ClampedArray): boolean {
  for (let index = 3; index < data.length; index += 4) {
    if (data[index]! >= OPAQUE_ALPHA_THRESHOLD) return true;
  }
  return false;
}

/** Fast two-pass chamfer distance, accurate enough for antialiased preview and 300-DPI output. */
function alphaDistanceMap(
  source: ImagePixelBuffer,
  outerWidth: number,
  outerHeight: number,
  offsetX: number,
  offsetY: number,
): Float32Array {
  const distance = new Float32Array(outerWidth * outerHeight);
  distance.fill(Number.POSITIVE_INFINITY);
  for (let y = 0; y < source.height; y += 1) {
    for (let x = 0; x < source.width; x += 1) {
      if (source.data[(y * source.width + x) * 4 + 3]! >= OPAQUE_ALPHA_THRESHOLD) {
        distance[(y + offsetY) * outerWidth + x + offsetX] = 0;
      }
    }
  }

  const diagonal = Math.SQRT2;
  for (let y = 0; y < outerHeight; y += 1) {
    for (let x = 0; x < outerWidth; x += 1) {
      const index = y * outerWidth + x;
      let best = distance[index]!;
      if (x > 0) best = Math.min(best, distance[index - 1]! + 1);
      if (y > 0) {
        best = Math.min(best, distance[index - outerWidth]! + 1);
        if (x > 0) best = Math.min(best, distance[index - outerWidth - 1]! + diagonal);
        if (x + 1 < outerWidth) best = Math.min(best, distance[index - outerWidth + 1]! + diagonal);
      }
      distance[index] = best;
    }
  }
  for (let y = outerHeight - 1; y >= 0; y -= 1) {
    for (let x = outerWidth - 1; x >= 0; x -= 1) {
      const index = y * outerWidth + x;
      let best = distance[index]!;
      if (x + 1 < outerWidth) best = Math.min(best, distance[index + 1]! + 1);
      if (y + 1 < outerHeight) {
        best = Math.min(best, distance[index + outerWidth]! + 1);
        if (x + 1 < outerWidth) best = Math.min(best, distance[index + outerWidth + 1]! + diagonal);
        if (x > 0) best = Math.min(best, distance[index + outerWidth - 1]! + diagonal);
      }
      distance[index] = best;
    }
  }
  return distance;
}

function compositeSourceOver(
  source: ImagePixelBuffer,
  target: Uint8ClampedArray,
  targetWidth: number,
  offsetX: number,
  offsetY: number,
): void {
  for (let y = 0; y < source.height; y += 1) {
    for (let x = 0; x < source.width; x += 1) {
      const sourceIndex = (y * source.width + x) * 4;
      const targetIndex = ((y + offsetY) * targetWidth + x + offsetX) * 4;
      const sourceAlpha = source.data[sourceIndex + 3]! / 255;
      if (sourceAlpha <= 0) continue;
      const targetAlpha = target[targetIndex + 3]! / 255;
      const outputAlpha = sourceAlpha + targetAlpha * (1 - sourceAlpha);
      for (let channel = 0; channel < 3; channel += 1) {
        const premultiplied =
          source.data[sourceIndex + channel]! * sourceAlpha +
          target[targetIndex + channel]! * targetAlpha * (1 - sourceAlpha);
        target[targetIndex + channel] = outputAlpha > 0 ? premultiplied / outputAlpha : 0;
      }
      target[targetIndex + 3] = outputAlpha * 255;
    }
  }
}

function parseColour(value: string): readonly [number, number, number, number] {
  const colour = value.trim();
  const hex = colour.match(/^#([\da-f]{3,8})$/i)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4) {
      return [
        Number.parseInt(hex[0]! + hex[0]!, 16),
        Number.parseInt(hex[1]! + hex[1]!, 16),
        Number.parseInt(hex[2]! + hex[2]!, 16),
        hex.length === 4 ? Number.parseInt(hex[3]! + hex[3]!, 16) : 255,
      ];
    }
    if (hex.length === 6 || hex.length === 8) {
      return [
        Number.parseInt(hex.slice(0, 2), 16),
        Number.parseInt(hex.slice(2, 4), 16),
        Number.parseInt(hex.slice(4, 6), 16),
        hex.length === 8 ? Number.parseInt(hex.slice(6, 8), 16) : 255,
      ];
    }
  }

  const rgb = colour.match(
    /^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)(?:\s*,\s*(\d+(?:\.\d+)?))?\s*\)$/i,
  );
  if (rgb) {
    const alpha = rgb[4] === undefined ? 1 : Number(rgb[4]);
    return [
      clampByte(Number(rgb[1])),
      clampByte(Number(rgb[2])),
      clampByte(Number(rgb[3])),
      clampByte(alpha * 255),
    ];
  }

  // UI persists hexadecimal colours; a deterministic fallback is safer than
  // allowing browser CSS parsing to diverge from the production renderer.
  return [0, 0, 0, 255];
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(Number.isFinite(value) ? value : 0)));
}
